import { Box, Button, Checkbox, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';
import { ISurveyQuestionGroupItem, IVisibilityCondition } from '../../../../shared/services/survey/survey.model';
import { ALL_CONDITION_OPS, ANSWERED_ANY, OP_LABELS, choiceOptionsLookupFor } from '../../../../shared/utils/survey-rules/condition-utils';

interface Props {
  conditions: IVisibilityCondition[];
  onChange: (next: IVisibilityCondition[]) => void;
  /** Questions a condition may reference: preceding items for display conditions, all items for rule conditions. */
  candidateItems: ISurveyQuestionGroupItem[];
  disabled?: boolean;
  /** Shown when there are no conditions yet. */
  emptyText?: string;
}

/**
 * The one editor for answer conditions, shared by question display conditions and
 * rule "only calculate when" conditions. Emits raw edits; run sanitizeConditions()
 * from condition-utils before persisting.
 */
export const ConditionBuilder = ({ conditions, onChange, candidateItems, disabled = false, emptyText }: Props) => {
  const choiceOptions = choiceOptionsLookupFor(candidateItems);

  const add = () => onChange([
    ...conditions,
    { questionId: candidateItems[0]?.surveyQuestionId ?? '', op: 'answered', value: undefined },
  ]);

  const update = (idx: number, patch: Partial<IVisibilityCondition>) =>
    onChange(conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  const remove = (idx: number) => onChange(conditions.filter((_, i) => i !== idx));

  return (
    <Stack spacing={1.5}>
      {conditions.length === 0 && emptyText && (
        <Typography variant='body2' color='text.secondary'>{emptyText}</Typography>
      )}

      {conditions.map((c, idx) => {
        const opts = choiceOptions(c.questionId);
        const isChoice = opts.length > 0;
        const showValue = c.op !== 'answered';

        return (
          <Stack key={idx} direction='row' spacing={1} alignItems='flex-start' flexWrap='wrap' sx={{ rowGap: 1 }}>
            <FormControl size='small' sx={{ minWidth: 200, flex: 1 }} disabled={disabled}>
              <InputLabel>Question</InputLabel>
              <Select
                label='Question'
                value={c.questionId}
                onChange={e => update(idx, { questionId: e.target.value, value: undefined })}
              >
                {candidateItems.map(item => (
                  <MenuItem key={item.surveyQuestionId} value={item.surveyQuestionId}>
                    {item.surveyQuestion?.prompt ?? item.surveyQuestionId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size='small' sx={{ width: 140 }} disabled={disabled}>
              <InputLabel>Condition</InputLabel>
              <Select
                label='Condition'
                value={c.op}
                onChange={e => update(idx, { op: e.target.value as IVisibilityCondition['op'], value: undefined })}
              >
                {ALL_CONDITION_OPS.map(op => (
                  <MenuItem key={op} value={op}>{OP_LABELS[op]}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {c.op === 'answered' && isChoice && (
              <FormControl size='small' sx={{ minWidth: 200, flex: 1 }} disabled={disabled}>
                <InputLabel>Which answer?</InputLabel>
                <Select
                  label='Which answer?'
                  value={typeof c.value === 'string' && c.value && c.value !== ANSWERED_ANY ? c.value : ANSWERED_ANY}
                  onChange={e => update(idx, { value: e.target.value === ANSWERED_ANY ? undefined : e.target.value })}
                >
                  <MenuItem value={ANSWERED_ANY}>Any selected option</MenuItem>
                  {opts.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}

            {c.op === 'answered' && !isChoice && (
              <Typography variant='body2' color='text.secondary' sx={{ alignSelf: 'center', flex: 1 }}>
                Any answer counts
              </Typography>
            )}

            {showValue && isChoice && (c.op === 'equals' || c.op === 'notEquals') && (
              <FormControl size='small' sx={{ minWidth: 200, flex: 1 }} disabled={disabled}>
                <InputLabel>Option</InputLabel>
                <Select
                  label='Option'
                  value={typeof c.value === 'string' ? c.value : ''}
                  onChange={e => update(idx, { value: e.target.value })}
                >
                  {opts.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}

            {showValue && isChoice && c.op === 'in' && (
              <FormControl size='small' sx={{ minWidth: 200, flex: 1 }} disabled={disabled}>
                <InputLabel>Options (match any)</InputLabel>
                <Select
                  label='Options (match any)'
                  multiple
                  value={Array.isArray(c.value) ? c.value : []}
                  onChange={e => update(idx, { value: e.target.value as string[] })}
                  renderValue={sel => (sel as string[]).map(id => opts.find(o => o.id === id)?.label ?? id).join(', ')}
                >
                  {opts.map(o => (
                    <MenuItem key={o.id} value={o.id}>
                      <Checkbox checked={Array.isArray(c.value) && c.value.includes(o.id)} />
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {showValue && (!isChoice || (c.op !== 'equals' && c.op !== 'notEquals' && c.op !== 'in')) && (
              <TextField
                size='small'
                disabled={disabled}
                label={c.op === 'in' ? 'Values (comma-separated)' : 'Value'}
                value={
                  c.op === 'in' && !isChoice
                    ? (Array.isArray(c.value) ? c.value.join(', ') : (c.value as string) ?? '')
                    : (c.value as string | number) ?? ''
                }
                onChange={e => {
                  if (c.op === 'in') {
                    // Store raw text while typing; sanitizeConditions splits, trims and filters it.
                    update(idx, { value: e.target.value });
                  } else if (c.op === 'gt' || c.op === 'lt' || c.op === 'gte' || c.op === 'lte') {
                    update(idx, { value: e.target.value === '' ? '' : Number(e.target.value) });
                  } else {
                    update(idx, { value: e.target.value });
                  }
                }}
                type={c.op === 'gt' || c.op === 'lt' || c.op === 'gte' || c.op === 'lte' ? 'number' : 'text'}
                sx={{ minWidth: 150, flex: 1 }}
              />
            )}

            <IconButton size='small' onClick={() => remove(idx)} aria-label='Remove condition' disabled={disabled}>
              <DeleteOutline fontSize='small' />
            </IconButton>
          </Stack>
        );
      })}

      <Box>
        <Button size='small' startIcon={<AddOutlined />} onClick={add} disabled={disabled}>
          Add condition
        </Button>
      </Box>
    </Stack>
  );
};
